// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { VsPageLayout } from "./VsPageLayout";
import { Check, X, ArrowRight, Sparkles, Globe } from "lucide-react";
import { Button } from "@core/components/ui/button";

// Framadate is a real product with its own identity; its accent stays a
// literal green rather than tracking our theme, unlike the DooDates-branded
// primary blue used throughout the rest of this comparison page.
const FRAMADATE_ICON_CLASS = "w-6 h-6 text-green-400"; // color-exempt
const FRAMADATE_CARD_CLASS =
  "p-6 rounded-xl border border-green-500/20 bg-green-500/5"; // color-exempt
const FRAMADATE_CARD_HEADING_CLASS = "font-semibold text-green-300 mb-2"; // color-exempt
const FRAMADATE_CHOOSE_HEADING_CLASS = "text-xl font-bold text-green-300 mb-4"; // color-exempt

const K = "polls:vs_framadate.";

const Yes = ({ label }: { label?: string }) => (
  <>
    <Check className="w-5 h-5 text-success inline" />
    {label ? ` ${label}` : null}
  </>
);
const No = () => <X className="w-5 h-5 text-muted-foreground inline" />;

export default function VsFramadate() {
  const { t } = useTranslation(["polls", "common"]);
  const k = (key: string) => t(`${K}${key}`);

  const rows: [string, React.ReactNode, React.ReactNode][] = [
    [
      "row_pricing",
      <Yes label={k("doodates_price")} />,
      <Yes label={k("free")} />,
    ],
    ["row_ai", <Yes label={k("yes")} />, <No />],
    [
      "row_mobile",
      <Yes label={k("modern_ux")} />,
      <Yes label={k("functional")} />,
    ],
    ["row_recurring", <Yes label={k("yes")} />, <No />],
    ["row_analytics", <Yes label={k("advanced")} />, <No />],
    ["row_open_source", <No />, <Yes label={k("yes")} />],
    ["row_languages", <Yes label="3+" />, <Yes label="11+" />],
  ];
  const doodatesCards = ["ai", "ui", "recurring", "analytics"];
  const framadateCards = ["free", "oss", "lang", "self"];
  const criteria = ["c1", "c2", "c3", "c4"];

  return (
    <VsPageLayout
      seo={{ title: k("meta_title"), description: k("meta_description") }}
      heading="DooDates vs Framadate"
      intro={k("intro")}
    >
      {/* Quick Comparison */}
      <section className="mb-16 bg-muted/30 rounded-2xl p-8 border border-border">
        <h2 className="text-2xl font-bold mb-8">{k("quick_title")}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-semibold">
                  {k("col_feature")}
                </th>
                <th className="text-center py-3 px-4 font-semibold">
                  DooDates
                </th>
                <th className="text-center py-3 px-4 font-semibold">
                  Framadate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map(([key, doodates, framadate]) => (
                <tr key={key}>
                  <td className="py-3 px-4">{k(key)}</td>
                  <td className="text-center">{doodates}</td>
                  <td className="text-center">{framadate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* DooDates Advantages */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-primary" />
          {k("why_doodates")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {doodatesCards.map((c) => (
            <div
              key={c}
              className="p-6 rounded-xl border border-primary/20 bg-primary/5"
            >
              <h3 className="font-semibold text-primary mb-2">
                {k(`dd_${c}_title`)}
              </h3>
              <p className="text-muted-foreground text-sm">
                {k(`dd_${c}_desc`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Framadate Advantages */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-8 flex items-center gap-2">
          <Globe className={FRAMADATE_ICON_CLASS} />
          {k("framadate_strengths")}
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {framadateCards.map((c) => (
            <div key={c} className={FRAMADATE_CARD_CLASS}>
              <h3 className={FRAMADATE_CARD_HEADING_CLASS}>
                {k(`fd_${c}_title`)}
              </h3>
              <p className="text-muted-foreground text-sm">
                {k(`fd_${c}_desc`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* When to Choose */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-8">{k("when_title")}</h2>
        <div className="grid sm:grid-cols-2 gap-8">
          {(
            [
              ["dd", "choose_doodates", "text-xl font-bold text-primary mb-4"],
              ["fd", "choose_framadate", FRAMADATE_CHOOSE_HEADING_CLASS],
            ] as const
          ).map(([prefix, heading, headingClass]) => (
            <div key={prefix}>
              <h3 className={headingClass}>{k(heading)}</h3>
              <ul className="space-y-3">
                {criteria.map((c) => (
                  <li key={c} className="flex gap-2">
                    <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">
                      {k(`${prefix}_${c}`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="text-center">
        <div className="mb-8 p-8 rounded-2xl bg-primary/10 border border-primary/20">
          <h2 className="text-2xl font-bold mb-4">{k("cta_title")}</h2>
          <p className="text-muted-foreground mb-6">{k("cta_desc")}</p>
          <Link to="/">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground px-8">
              {k("cta_button")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </VsPageLayout>
  );
}
